import React, { useState, useEffect } from 'react';
import { Station } from '../types/Station';
import { fetchStations, createStation, deleteStation } from '../api/apis';

export const StationsView = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load stations on component mount
  useEffect(() => {
    loadStations();
  }, []);

  // Function to load stations from API
  const loadStations = async () => {
    setIsLoading(true);
    try {
      const fetchedStations = await fetchStations();
      setStations(fetchedStations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stations.');
      console.error('Error loading stations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new station
  const handleAddStation = async (name: string) => {
    if (!name.trim()) return;
    
    try {
      const station: Station = {
        id: "tmp",
        name: name.trim()
      };
      await createStation(station);
      // Fetch updated stations instead of reloading
      const updatedStations = await fetchStations();
      setStations(updatedStations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add station');
      console.error('Error adding station:', err);
    }
  };

  // Delete a station
  const handleDeleteStation = async (stationId: string) => {
    try {
      await deleteStation(stationId);
      // Update state directly by filtering out the deleted station
      setStations(prevStations => prevStations.filter(station => station.id !== stationId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete station');
      console.error('Error deleting station:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-gray-600">Loading stations...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Stations Management</h1>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Stations</h2>
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              id="newStationName"
              placeholder="New station name"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => {
                const input = document.getElementById('newStationName') as HTMLInputElement;
                if (input.value.trim()) {
                  handleAddStation(input.value.trim());
                  input.value = '';
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Add Station
            </button>
          </div>
          <div className="space-y-2">
            {stations.map((station) => (
              <div
                key={station.id}
                className="p-3 rounded border border-gray-200 hover:border-blue-300 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium">{station.name}</span>
                  <button
                    onClick={() => handleDeleteStation(station.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 